import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Wordmark } from '../../components/brand/Wordmark';
import { useAuth } from '../../auth/AuthContext';
import { ApiClientError } from '../../lib/api';

export function LoginScreen() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.status === 429) setError("Too many attempts. Try again in a few minutes.");
        else if (err.status === 401) setError('Email or password not recognized.');
        else setError('Something went wrong. Try again.');
      } else {
        setError('Network error. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main id="main-content" className="container" style={{ paddingBlock: 'var(--space-7)' }}>
      <div style={{ paddingBlockStart: 'var(--space-7)', paddingBlockEnd: 'var(--space-6)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Wordmark variant="stacked" size="large" />
        <p className="muted" style={{ marginBlockStart: 'var(--space-2)' }}>
          A taper tracker that gets it
        </p>
      </div>

      <form className="stack" onSubmit={onSubmit} style={{ '--stack-gap': 'var(--space-4)' } as React.CSSProperties}>
        <Input
          label="Email"
          type="email"
          autoComplete="username"
          inputMode="email"
          enterKeyHint="next"
          autoCapitalize="off"
          spellCheck={false}
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <div className="field">
          <label className="field-label" htmlFor="pw">
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              id="pw"
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              enterKeyHint="go"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="field-control"
              style={{ paddingInlineEnd: '60px' }}
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              style={{
                position: 'absolute',
                insetInlineEnd: 0,
                insetBlockStart: 0,
                blockSize: '100%',
                paddingInline: 'var(--space-3)',
                color: 'var(--color-ink-secondary)',
                fontSize: 'var(--text-caption)',
              }}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              {showPw ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        {error ? (
          <div className="field-error" role="alert">
            {error}
          </div>
        ) : null}

        <Button type="submit" size="lg" block loading={busy} disabled={!email || !password}>
          Sign in
        </Button>

        <Button
          type="button"
          variant="secondary"
          size="lg"
          block
          onClick={() => navigate('/register')}
        >
          Register now
        </Button>
      </form>

      <div style={{ textAlign: 'center', marginBlockStart: 'var(--space-4)' }}>
        <Link to="/register" className="text-caption" style={{ color: 'var(--color-accent)' }}>
          New here? Create an account →
        </Link>
      </div>

      <p className="dim text-caption" style={{ marginBlockStart: 'var(--space-7)', textAlign: 'center' }}>
        Your data lives in a private encrypted store. Nothing is shared.
      </p>
    </main>
  );
}
